import { PencilAltIcon } from "@heroicons/react/outline";
import { useSession } from "next-auth/react";
import Link from "next/link";
import LocationIcon from "../../../../components/Icons/LocationIcon";
import { cities as citiesList } from "../../../../../common/constants";

type HeroProps = {
    meta: {
        companyLogo: string;
        company: string;
        companySlug: string;
        cities?: string;
        tags: [];
    };
    title: string;
    slug: string;
    tags: {
        name: string;
        slug: string;
    }[];
    cities: {
        name: string;
        slug: string;
    }[];
    premium: boolean;
};
export default function Hero({
    meta,
    title,
    slug,
    tags,
    cities,
    premium,
}: HeroProps) {
    const { data: session } = useSession();
    const isCompanyOwner = session?.company?.slug === meta.companySlug;
    const isAdmin = session?.user?.role === "ADMIN";

    const renderCities = (city, i) => {
        const cityExist = citiesList.findIndex((cty) => cty.slug === city.slug);
        if (cityExist > -1) {
            return (
                <Link href={`/lokacija/${city.slug}/1`}>
                    <a>
                        <span className="inline-block mx-2 hover:text-blue hover:underline">
                            {city.name}
                        </span>{" "}
                        {i + 1 < cities.length && "|"}
                    </a>
                </Link>
            );
        } else {
            return (
                <>
                    <span className="inline-block mx-2 hover:text-blue hover:underline">
                        {city.name}
                    </span>{" "}
                    {i + 1 < cities.length && "|"}
                </>
            );
        }
    };
    return (
        <section className="bg-gray-light">
            <div className="pt-10 pb-6 md:pt-20 text-center max-w-screen-sm m-auto px-6 lg:px-0">
                <div className="relative mb-4">
                    <img
                        src={meta.companyLogo}
                        className={`w-16 block m-auto rounded-full ${
                            premium && "-mb-1"
                        }`}
                    />
                    {premium && (
                        <span className="-mt-2 text-xs bg-premium-300 text-white px-2 py-1 rounded-md">
                            premium
                        </span>
                    )}
                </div>
                <h2 className="mb-3 text-2xl text-blue font-medium inline-block hover:underline">
                    <Link href={`/kompanija/${meta.companySlug}`}>
                        <a>
                            {meta.company}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 inline align-top"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                            </svg>
                        </a>
                    </Link>
                </h2>
                <h1 className="md:text-4xl leading-tight pb-4 font-semibold flex justify-center">
                    {title}
                    {(isAdmin || isCompanyOwner) && (
                        <Link href={`/kreiraj/posao?slug=${slug}`}>
                            <a>
                                <PencilAltIcon className="w-6 ml-2" />
                            </a>
                        </Link>
                    )}
                </h1>
                <div className="mb-8 font-medium flex justify-center items-center">
                    <LocationIcon />
                    {cities
                        .filter(
                            (cty) =>
                                cty?.slug !== "novi-sad" &&
                                cty?.slug !== "beograd" &&
                                cty?.slug !== "zagreb"
                        )
                        .map((city, i) => renderCities(city, i))}
                </div>
                <p className="font-normal text-gray-dark">
                    {tags.map((tag) => (
                        <Link href={`/tag/${tag.slug}/1`}>
                            <a>
                                <span className="inline-block mx-2 hover:text-blue underline">
                                    {tag.name}
                                </span>
                            </a>
                        </Link>
                    ))}
                </p>
            </div>
        </section>
    );
}
